import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import { uiState } from 'react-redux-ui-state';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableContainer from '@material-ui/core/TableContainer';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Collapse from '@material-ui/core/Collapse';
import CardActions from '@material-ui/core/CardActions';
import Button from '@material-ui/core/Button';
import SettingsDropDown from './SettingsDropDown/SettingsDropDown';
import SettingsRowItem from './SettingsRowItem/SettingsRowItem';
import FlagEnabled from '../../../../../FlagEnabled/FlagEnabledContainer';
import ORTable from '../../../../../ORTable/ORTable';
import ORTypography from '../../../../../ORTypography/ORTypography';

// Django converts dictionary keys from sneak_case to camelCase,
// so we lower case everything and remove underscore with this function.
// NOTE: str.replace only changes first occurrence but in cases
// with_multiple_occurrences RegExp is necessary with the `greed` flag.
const fixCase = (name) => name.toLowerCase().replace(RegExp('_', 'g'), '');

export const removeUsedItems = (catalogConfigurations, instanceConfig) => {
  const tempInstanceConfig = Object.keys(instanceConfig).map((item) => fixCase(item));
  return catalogConfigurations.filter((item) => tempInstanceConfig.indexOf(fixCase(item.name)) < 0);
};

export const filterCfg = (catalog, keyName) =>
  catalog.filter((config) => fixCase(config.name) === fixCase(keyName));

const propTypes = {
  id: PropTypes.string.isRequired,
  setUiState: PropTypes.func.isRequired,
  classes: PropTypes.shape({}).isRequired,
  serviceSubtype: PropTypes.string.isRequired,
  service: PropTypes.string.isRequired,
  instanceSettings: PropTypes.shape({
    datastoreConfigurations: PropTypes.shape({}),
  }).isRequired,
  catalogConfigurations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getCatalogConfiguration: PropTypes.func.isRequired,
  setInstanceConfiguration: PropTypes.func.isRequired,
  flags: PropTypes.shape({}),
  newConfig: PropTypes.shape({}).isRequired,
};

const defaultProps = {
  flags: {
    settings: false,
  },
};

const allowConfig = ['timescaledb_cluster', 'postgresql_cluster', 'elasticsearch_cluster'];

export function UnstyledConfigSection({
  id,
  classes,
  serviceSubtype,
  instanceSettings,
  flags,
  catalogConfigurations,
  service,
  getCatalogConfiguration,
  setInstanceConfiguration,
  setUiState,
  newConfig,
}) {
  useEffect(() => {
    getCatalogConfiguration(serviceSubtype);
  }, [serviceSubtype, getCatalogConfiguration]);

  if (allowConfig.indexOf(serviceSubtype.toLowerCase()) < 0) {
    return null;
  }

  let activeSettings = {};
  if (instanceSettings.datastoreConfigurations) {
    activeSettings = { ...instanceSettings.datastoreConfigurations };
  }

  /* new values at the bottom, so editing a value can change an old value */
  const oldAndNewSettings = { ...activeSettings, ...newConfig };

  const filteredUnusedCatalogConfig = removeUsedItems(catalogConfigurations, oldAndNewSettings);

  const addConfig = (keyName, keyValue = null) => {
    // If an item is added for the first time, set keyValue to null and we will automatically
    // get the default value, thus we implemented the reading of the default for multiple types.
    // If the item is edited, then set keyValue to desired value and we will ignore default value.
    setUiState({
      newConfig: {
        ...newConfig /* Allow multiple configurations to be pending confirmation */,
        [keyName]:
          keyValue ||
          filterCfg(catalogConfigurations, keyName)[0].value.default ||
          filterCfg(catalogConfigurations, keyName)[0].value.enum[0],
      },
    });
  };

  const clear = () => {
    setUiState({
      newConfig: {},
    });
  };

  const save = (items2save) => {
    // Delete will triggers the save with all old(active items) minus the deleted item since we
    // don't want to patch the new items because those should only be sent on confirmation - witch
    // calls save ALL cfgs to save the merge between new(unsaved items) and
    // old(active items configuration); that is oldAndNewSettings
    const tempData = { settings: { datastoreConfigurations: { ...items2save } } };
    setInstanceConfiguration(id, service, tempData);
  };

  const deleteConfig = (keyName) => {
    // The key is an item that was never saved or it is being edited
    if (keyName in newConfig) {
      const temp = { ...newConfig };
      delete temp[keyName];
      setUiState({
        newConfig: { ...temp },
      });
    }
    // The key is an item that was active in the instance
    if (keyName in activeSettings) {
      activeSettings[keyName] = null;
      save(activeSettings);
    }
  };

  const subtitleWithStar = (
    <ORTypography className={classes.subHeader}>
      Configurations marked with an asterisk (<span style={{ color: 'red' }}>*</span>) will trigger
      instance to restart
    </ORTypography>
  );

  const instaceSettingsTable = () => (
    <TableContainer>
      <ORTable classes={{ table: classes.table }}>
        <TableBody>
          {Object.keys(oldAndNewSettings).map((key) => (
            <TableRow key={key}>
              <SettingsRowItem
                key={key}
                keyName={key}
                keyValue={oldAndNewSettings[key]}
                initialValues={oldAndNewSettings}
                catalogItem={filterCfg(catalogConfigurations, key)[0]}
                addConfig={addConfig}
                deleteConfig={deleteConfig}
              />
            </TableRow>
          ))}
        </TableBody>
      </ORTable>
    </TableContainer>
  );

  return (
    <FlagEnabled flags={flags} flagKey="settings">
      <div className={classes.configurationArea}>
        <div className={classes.listContainer}>
          <Card>
            <CardHeader
              title={
                <ORTypography component="h2" variant="h5">
                  Custom Configuration
                </ORTypography>
              }
              subheader={subtitleWithStar}
            />

            {/* Dropdown With Catalog configuration */}
            <CardContent>
              <SettingsDropDown
                catalogConfigurations={filteredUnusedCatalogConfig}
                addConfig={addConfig}
              />
            </CardContent>

            {/* Table with configuration items in use or being edited */}
            <CardContent className={classes.tableContent}>{instaceSettingsTable()}</CardContent>

            {/* Confirm/Cancel section */}
            <Collapse in={Object.keys(newConfig).length > 0} timeout={1500}>
              <CardActions>
                <Button
                  name="cancel"
                  className={classes.cancelButton}
                  onClick={clear}
                  variant="contained"
                >
                  CANCEL
                </Button>
                <Button
                  name="cancel"
                  className={classes.confirmButton}
                  onClick={() => save(oldAndNewSettings)}
                  variant="contained"
                >
                  CONFIRM
                </Button>
              </CardActions>
            </Collapse>
          </Card>
        </div>
      </div>
    </FlagEnabled>
  );
}

UnstyledConfigSection.propTypes = propTypes;
UnstyledConfigSection.defaultProps = defaultProps;

const styles = (theme) => ({
  table: {
    border: 'unset',
    borderRadius: 'unset',
  },
  subHeader: {
    color: '#6C6D6D',
    font: 'italic 12px Roboto',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    alignItems: 'normal',
  },
  configurationArea: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  tableContent: {
    paddingLeft: '0px',
    paddingRight: '0px',
    paddingBottom: '0px !important',
  },
  cancelButton: {
    flexGrow: 0.1,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: theme.customColors.white,
    backgroundColor: theme.customColors.teal,
    '&:hover': {
      background: theme.customColors.teal,
    },
  },
  confirmButton: {
    flexGrow: 0.1,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: theme.customColors.white,
    backgroundColor: theme.customColors.orange,
    '&:hover': {
      background: theme.customColors.orange,
    },
  },
});

export const uiConfig = {
  name: 'ConfigSection',
  state: () => ({
    newConfig: {},
  }),
};

export default uiState(uiConfig)(withStyles(styles)(UnstyledConfigSection));
